import { useContext, useEffect, useState, type ChangeEvent } from "react";
import { Box, TextField, Checkbox } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import { useLocation } from "react-router-dom";
import PrefectureSelect from "../../../../components/PrefectureSelect";
import CustomerSelect from "../../../../components/CustomerSelect";
import BranchSelect from "../../../../components/BranchSelect";
import SearchResult from "./SearchResult";
import { IsSSAgent } from "../../../../role";
import { TokenContext } from "../../../../TokenContext";
import type { SsListSearchContents } from "../../../../types/disaster";

export default function SearchArea() {
  const searchParams = new URLSearchParams(useLocation().search);
  const disasterId = searchParams.get("disaster-id");

  const [searchContents, setSearchContents] = useState<SsListSearchContents>({
    display_id: disasterId,
    display_code: null,
    display_code_disabled: false,
    name: null,
    pref: null,
    m_branch_id: null,
    m_customer_id: null,
    latest_sales: null,
    is_input: null,
    is_damage: null,
  });

  const tokenContext = useContext(TokenContext);
  const user = tokenContext?.user;
  const isSSAgent = user ? IsSSAgent(user.m_organization.m_role_id) : false;

  useEffect(() => {
    if (isSSAgent && user?.site_users) {
      const ownSites = user.site_users.map((su) => su.site);
      if (ownSites.length > 0) {
        setSearchContents((prev) => ({
          ...prev,
          display_code: ownSites[0].display_code as string | null,
          display_code_disabled: true,
        }));
      }
    }
  }, [isSSAgent, user]);

  const handleChangeLocathion = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchContents((prev) => ({
      ...prev,
      display_code: e.target.value,
    }));
  };

  const handleChangePref = (prefCodes: string[]) => {
    setSearchContents((prev) => ({
      ...prev,
      pref: prefCodes.length > 0 ? prefCodes[0] : null,
    }));
  };

  const handleChangeBranch = (values: number[]) => {
    setSearchContents((prev) => ({
      ...prev,
      m_branch_id: values.length > 0 ? values[0] : null,
    }));
  };

  const handleChangeCustomer = (values: number[]) => {
    setSearchContents((prev) => ({
      ...prev,
      m_customer_id: values.length > 0 ? values[0] : null,
    }));
  };

  const handleChangeIsInput = (value: boolean) => {
    setSearchContents((prev) => ({
      ...prev,
      is_input: prev.is_input === value ? null : value,
    }));
  };

  const handleChangeIsDamage = (value: boolean) => {
    setSearchContents((prev) => ({
      ...prev,
      is_damage: prev.is_damage === value ? null : value,
    }));
  };

  const handleChangeSales = (value: boolean) => {
    setSearchContents((prev) => ({
      ...prev,
      latest_sales: prev.latest_sales === value ? null : value,
    }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        justifyContent: "center",
        borderRadius: 2,
        borderColor: "primary.main",
        boxShadow: 3,
        color: "outline.dark",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "fit-content",
          justifyContent: "center",
          gap: "16px",
          paddingTop: "32px",
          paddingRight: "40px",
          paddingLeft: "40px",
          "@media screen and (max-width:900px)": {
            padding: "16px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: "min-content",
            paddingLeft: "40px",
            fontSize: 16,
            fontWeight: "bold",
            color: "#000",
            whiteSpace: "nowrap",
            "@media screen and (max-width:900px)": {
              padding: "0",
            },
          }}
        >
          検索条件
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            "@media screen and (max-width:600px)": {
              flexDirection: "column",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              width: "100%",
              maxWidth: "480px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>Sコード</Box>
              <Box sx={{ display: "flex", width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px",
                    height: "40px",
                    border: "1px solid #CCC",
                    borderRight: "none",
                    borderTopLeftRadius: "4px",
                    borderBottomLeftRadius: "4px",
                  }}
                >
                  S
                </Box>
                <TextField
                  id="outlined-basic"
                  variant="outlined"
                  value={searchContents.display_code || ""}
                  disabled={searchContents.display_code_disabled}
                  onChange={handleChangeLocathion}
                  sx={{
                    height: "40px",
                    width: "100%",
                    ".MuiInputBase-root": {
                      height: "100%",
                    },
                    "#outlined-basic": {
                      boxSizing: "border-box",
                      height: "100%",
                    },
                    ".MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderRadius: "0 4px 4px 0",
                      },
                    },
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>都道府県</Box>
              <PrefectureSelect
                branchId={searchContents.m_branch_id ?? null}
                customerId={searchContents.m_customer_id ?? null}
                changedValue={handleChangePref}
                isMultiple={false}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>特約店</Box>
              <CustomerSelect
                prefCode={searchContents.pref ?? null}
                branchId={searchContents.m_branch_id ?? null}
                changedValue={handleChangeCustomer}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>支店</Box>
              <BranchSelect
                prefCode={searchContents.pref ?? null}
                customerId={searchContents.m_customer_id ?? null}
                changedValue={handleChangeBranch}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              width: "100%",
              maxWidth: "480px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>被害報告入力有無</Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={searchContents.is_input === true}
                    onChange={() => handleChangeIsInput(true)}
                  />
                  <PanoramaFishEyeIcon />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={searchContents.is_input === false}
                    onChange={() => handleChangeIsInput(false)}
                  />
                  <CloseIcon />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>被害有無</Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={searchContents.is_damage === true}
                    onChange={() => handleChangeIsDamage(true)}
                  />
                  <PanoramaFishEyeIcon />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={searchContents.is_damage === false}
                    onChange={() => handleChangeIsDamage(false)}
                  />
                  <CloseIcon />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "min-content",
                fontSize: 16,
                fontWeight: "bold",
                color: "#000",
                whiteSpace: "nowrap",
                "@media screen and (max-width:900px)": {
                  flexDirection: "column",
                  width: "100%",
                  alignItems: "flex-start",
                },
              }}
            >
              <Box sx={{ minWidth: "160px" }}>直近売上</Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={searchContents.latest_sales === true}
                    onChange={() => handleChangeSales(true)}
                  />
                  <PanoramaFishEyeIcon />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={searchContents.latest_sales === false}
                    onChange={() => handleChangeSales(false)}
                  />
                  <CloseIcon />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <SearchResult searchContent={searchContents} />
    </Box>
  );
}
